import { zodResolver } from "@hookform/resolvers/zod";
import { Download } from "@mui/icons-material";
import { Button, Grid } from "@mui/material";
import { XLSX_MIME_TYPE } from "@repo/utils";
import { toDate } from "@repo/utils/date";
import type { DeliveryFilterSchemaType } from "@schemas/DeliveryForm/DeliveryFilterSchema";
import DeliveryFilterSchema, {
  DeliveryFilterDefaultValues,
} from "@schemas/DeliveryForm/DeliveryFilterSchema";
import type { DeliveryType } from "@types_def/models/delivery.types";
import { UserRole } from "@types_def/models/user.type";
import { deliveryTypesForUiFilter } from "@utils/constants/DeliveryType/deliveryTypeAttributes";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import type { z } from "zod";
import { exportDeliveriesAdmins, exportDeliveriesPartners } from "@/api/Rest/exports.queries";
import { downloadBuffer } from "@/utils/download";

import DeliveryTableCountryFilter from "./Delivery/DeliveryTable/Filters/CountryFilter";
import DeliveryTableDeliveryDateFilter from "./Delivery/DeliveryTable/Filters/DateFilter";
import OrganizationFilter from "./Delivery/DeliveryTable/Filters/OrganizationFilter";
import DeliveryTableServiceFilter from "./Delivery/DeliveryTable/Filters/ServiceFilter";
import DeliveryTableTypeFilter from "./Delivery/DeliveryTable/Filters/TypeFilter";

const EXPORT_CONFIG = {
  [UserRole.ADMIN]: {
    fn: exportDeliveriesAdmins,
    filename: "deliveries-admins.xlsx",
  },
  [UserRole.CLIENT]: {
    fn: exportDeliveriesPartners,
    filename: "deliveries-partners.xlsx",
  },
} as const satisfies Partial<
  Record<
    UserRole,
    {
      fn: (data: Parameters<typeof exportDeliveriesAdmins>[0]) => Promise<{ data: Blob }>;
      filename: string;
    }
  >
>;

type ExportFilterProps = {
  onCancel: () => void;
  userRole: UserRole;
  onError?: (error: unknown) => void;
};

function getDefaultValuesFromSearchParams(searchParams: URLSearchParams): DeliveryFilterSchemaType {
  const deliveryType = searchParams.get("deliveryType");
  const deliveryServices = searchParams.get("deliveryServices");
  const deliveryCountries = searchParams.get("deliveryCountries");
  const carDeliveryDeadlineFrom = searchParams.get("carDeliveryDeadlineFrom");
  const carDeliveryDeadlineTo = searchParams.get("carDeliveryDeadlineTo");
  const organizationSlug = searchParams.get("organizationSlug");

  const hasText = (value: string | null): value is string => value !== null && value.length > 0;
  const allowedDeliveryTypes = new Set(deliveryTypesForUiFilter);
  const parseTimestamp = (value: string | null): Date | null => {
    if (!hasText(value)) return null;

    return toDate(Number.parseInt(value, 10));
  };

  return {
    deliveryType: hasText(deliveryType)
      ? deliveryType.split(",").reduce(
          (acc, type) => {
            if (allowedDeliveryTypes.has(type as DeliveryType)) {
              acc[type as DeliveryType] = true;
            }
            return acc;
          },
          { ...DeliveryFilterDefaultValues.deliveryType }
        )
      : DeliveryFilterDefaultValues.deliveryType,
    deliveryCountries: hasText(deliveryCountries)
      ? deliveryCountries.split(",").reduce<Record<string, boolean>>((acc, country) => {
          acc[country] = true;
          return acc;
        }, {})
      : DeliveryFilterDefaultValues.deliveryCountries,
    deliveryServices: hasText(deliveryServices)
      ? deliveryServices.split(",").reduce<Record<string, boolean>>((acc, service) => {
          acc[service] = true;
          return acc;
        }, {})
      : DeliveryFilterDefaultValues.deliveryServices,
    carDeliveryDeadlineFrom:
      parseTimestamp(carDeliveryDeadlineFrom) ??
      DeliveryFilterDefaultValues.carDeliveryDeadlineFrom,
    carDeliveryDeadlineTo:
      parseTimestamp(carDeliveryDeadlineTo) ?? DeliveryFilterDefaultValues.carDeliveryDeadlineTo,
    organizationSlug: hasText(organizationSlug)
      ? organizationSlug.split(",")
      : DeliveryFilterDefaultValues.organizationSlug,
  };
}

function toCsvOrNull(values: string[]): string | null {
  const compact = values.filter(Boolean);
  return compact.length > 0 ? compact.join(",") : null;
}

function ExportFilterForm({ onCancel, userRole, onError }: ExportFilterProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const defaultValues = useMemo(
    () => getDefaultValuesFromSearchParams(searchParams),
    [searchParams]
  );

  const methods = useForm<z.input<typeof DeliveryFilterSchema>, unknown, DeliveryFilterSchemaType>({
    resolver: zodResolver(DeliveryFilterSchema),
    defaultValues,
  });

  const { isSubmitting } = methods.formState;

  const handleFormSubmit = useCallback(
    async (data: DeliveryFilterSchemaType) => {
      const config = EXPORT_CONFIG[userRole as keyof typeof EXPORT_CONFIG];

      if (config == null) {
        const err = new Error(`Export is not supported for role: ${userRole}`);
        onError?.(err);
        return;
      }

      try {
        const countries = data.deliveryCountries as Record<string, boolean> | undefined;
        const selectedCountries = Object.keys(countries ?? {}).filter((country) =>
          Boolean(countries?.[country])
        );

        // Keep the same compatibility behavior as the filters modal.
        if (selectedCountries.includes("FR")) selectedCountries.push("MC");
        if (selectedCountries.includes("BE")) selectedCountries.push("DE");

        const types = data.deliveryType as Record<string, boolean> | undefined;
        const selectedTypes = Object.keys(types ?? {}).filter((type) => Boolean(types?.[type]));

        const services = data.deliveryServices as Record<string, boolean> | undefined;
        const selectedServices = Object.keys(services ?? {}).filter((service) =>
          Boolean(services?.[service])
        );

        const response = await config.fn({
          deliveryCountries: toCsvOrNull(selectedCountries),
          deliveryType: toCsvOrNull(selectedTypes),
          deliveryServices: toCsvOrNull(selectedServices),
          organizationSlug: toCsvOrNull(data.organizationSlug ?? []),
          carDeliveryDeadlineFrom: data.carDeliveryDeadlineFrom ?? null,
          carDeliveryDeadlineTo: data.carDeliveryDeadlineTo ?? null,
        });

        const arrayBuffer = await response.data.arrayBuffer();
        downloadBuffer(arrayBuffer, config.filename, XLSX_MIME_TYPE);
        onCancel();
      } catch (error) {
        onError?.(error);
      }
    },
    [onCancel, onError, userRole]
  );

  return (
    <FormProvider {...methods}>
      <Grid
        container
        spacing={2}
        component="form"
        // eslint-disable-next-line ts/no-misused-promises
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        noValidate
      >
        <Grid size={12}>
          <DeliveryTableTypeFilter />
        </Grid>

        <Grid size={12}>
          <DeliveryTableCountryFilter />
        </Grid>

        <Grid size={12}>
          <DeliveryTableServiceFilter userRole={userRole} />
        </Grid>

        <Grid size={12}>
          <DeliveryTableDeliveryDateFilter />
        </Grid>

        {userRole === UserRole.ADMIN && (
          <Grid size={12}>
            <OrganizationFilter />
          </Grid>
        )}

        <Grid size={12} container spacing={2} justifyContent="flex-end">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t("common.actions.cancel")}
            </Button>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              startIcon={<Download fontSize="small" />}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("common.loading") : t("common.actions.download")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

export default ExportFilterForm;
